import { CarwashStat, CarWashStore } from '../../../stores/car-wash';
import { KpiCardProps } from '../components/kpi-card';

/**
 * Gets a stat value by name
 */
function getStatValue(
    stats: CarwashStat[] | null | undefined,
    name: string
): number {
    return (
        // case insensitive search
        stats?.find((s) => s.name.toLowerCase() === name.toLowerCase())
            ?.value || 0
    );
}

/**
 * The seven groups on this page that are NOT on the KpiCardSpec card path.
 * All of them read `carWashStore.transactionStats` (revenue, subscription
 * plans, refunds), a different endpoint from the analytics envelope, and are
 * out of scope for this migration. "Carwash engagement" is not one of them: it
 * renders through `ENGAGEMENT_CARDS` (see `./cards`).
 */
export function getAllCarwashKpis(carWashStore: CarWashStore): KpiCardProps[] {
    const stats = carWashStore.transactionStats;
    return [
        {
            title: 'Revenue breakdown',
            isLoading: carWashStore.isFetchingTransactionStats,
            kpis: [
                {
                    title: 'Total revenue',
                    value: getStatValue(stats, 'Total Revenue'),
                    isCurrency: true,
                },
                {
                    title: 'Single wash revenue',
                    value: getStatValue(stats, 'Single Wash Revenue'),
                    isCurrency: true,
                },
                {
                    title: 'Subscription revenue',
                    value: getStatValue(stats, 'Subscription Revenue'),
                    isCurrency: true,
                },
            ],
        },
        {
            title: 'Distinct users by plan type',
            isLoading: carWashStore.isFetchingTransactionStats,
            kpis: [
                {
                    title: 'Family & Business subscriptions',
                    value:
                        getStatValue(
                            stats,
                            'Family & Business subscriptions - New subscribers'
                        ) +
                        getStatValue(
                            stats,
                            'Family & Business subscriptions - Renewing subscribers'
                        ),
                },
                {
                    title: 'Single subscription plans',
                    value:
                        getStatValue(
                            stats,
                            'Single subscription plans - New subscribers'
                        ) +
                        getStatValue(
                            stats,
                            'Single subscription plans - Renewing subscribers'
                        ),
                },
                {
                    title: 'Bundles and single washes',
                    value: getStatValue(
                        stats,
                        'Bundle and single washes - Distinct users'
                    ),
                },
            ],
        },
        {
            title: 'Family & Business subscriptions (New)',
            isLoading: carWashStore.isFetchingTransactionStats,
            kpis: [
                {
                    title: 'New subscribers',
                    value: getStatValue(
                        stats,
                        'Family & Business subscriptions - New subscribers'
                    ),
                },
                {
                    title: 'Revenue',
                    value: getStatValue(
                        stats,
                        'Family & Business subscriptions - New subscription revenue'
                    ),
                    isCurrency: true,
                },
            ],
        },
        {
            title: 'Family & Business subscriptions (Renewing)',
            isLoading: carWashStore.isFetchingTransactionStats,
            kpis: [
                {
                    title: 'Renewing subscribers',
                    value: getStatValue(
                        stats,
                        'Family & Business subscriptions - Renewing subscribers'
                    ),
                },
                {
                    title: 'Revenue',
                    value: getStatValue(
                        stats,
                        'Family & Business subscriptions - Renewal revenue'
                    ),
                    isCurrency: true,
                },
            ],
        },
        {
            title: 'Others',
            isLoading: carWashStore.isFetchingTransactionStats,
            kpis: [
                {
                    title: 'Upgrade revenue',
                    value: getStatValue(stats, 'Upgrade revenue'),
                    isCurrency: true,
                },
                {
                    title: 'Total refund amount',
                    value: getStatValue(stats, 'Refund Amount'),
                    isCurrency: true,
                },
            ],
        },
        {
            title: 'Single subscription plans (New)',
            isLoading: carWashStore.isFetchingTransactionStats,
            kpis: [
                {
                    title: 'New subscribers',
                    value: getStatValue(
                        stats,
                        'Single subscription plans - New subscribers'
                    ),
                },
                {
                    title: 'Revenue',
                    value: getStatValue(
                        stats,
                        'Single subscription plans - New subscription revenue'
                    ),
                    isCurrency: true,
                },
            ],
        },
        {
            title: 'Single subscription plans (Renewing)',
            isLoading: carWashStore.isFetchingTransactionStats,
            kpis: [
                {
                    title: 'Renewing subscribers',
                    value: getStatValue(
                        stats,
                        'Single subscription plans - Renewing subscribers'
                    ),
                },
                {
                    title: 'Revenue',
                    value: getStatValue(
                        stats,
                        'Single subscription plans - Renewal revenue'
                    ),
                    isCurrency: true,
                },
            ],
        },
    ];
}
